import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { EVENT_TYPES, BOOKING_PACKAGES } from "../../content/site";
import { http, apiError } from "../../lib/api";
import { useBooking } from "../../context/BookingContext";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const EMPTY = { name: "", phone: "", email: "", event_type: "", event_date: "", location: "", package: "", message: "" };
const field = "h-12 rounded-xl border-gold/25 bg-white/80 text-[15px] focus-visible:ring-gold";

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="mb-2 block text-[11px] uppercase tracking-[0.22em] text-ink-soft">{children}</label>
);

const Success = ({ name, onReset }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex min-h-[460px] flex-col items-center justify-center text-center" data-testid="booking-success">
    <CheckCircle2 className="h-14 w-14 text-gold" strokeWidth={1.2} />
    <p className="mt-6 font-script text-5xl text-gold">Thank you, {name}!</p>
    <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-ink">Your inquiry has been received. Kishan Patel will contact you shortly to discuss your special day.</p>
    <button onClick={onReset} className="btn-ghost mt-8" data-testid="booking-send-another">Send another inquiry</button>
  </motion.div>
);

export const BookingForm = () => {
  const { selection } = useBooking();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  useEffect(() => {
    if (!selection.pkg) return;
    setDone(null);
    setForm((f) => ({ ...f, package: selection.pkg, event_type: selection.eventType || f.event_type }));
  }, [selection]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.event_type) return toast.error("Please choose an event type");
    setLoading(true);
    try {
      await http.post("/inquiries", form);
      setDone(form.name.split(" ")[0]);
      setForm(EMPTY);
      toast.success("Inquiry sent! We'll be in touch soon.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Success name={done} onReset={() => setDone(null)} />;

  return (
    <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="booking-form">
      <div><Label htmlFor="bf-name">Your name *</Label><Input id="bf-name" required minLength={2} value={form.name} onChange={set("name")} className={field} placeholder="Full name" data-testid="booking-input-name" /></div>
      <div><Label htmlFor="bf-phone">Phone / WhatsApp *</Label><Input id="bf-phone" required type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} className={field} placeholder="+91" data-testid="booking-input-phone" /></div>
      <div><Label htmlFor="bf-email">Email</Label><Input id="bf-email" type="email" value={form.email} onChange={set("email")} className={field} placeholder="you@example.com" data-testid="booking-input-email" /></div>
      <div>
        <Label>Event type *</Label>
        <Select value={form.event_type} onValueChange={set("event_type")}>
          <SelectTrigger className={field} data-testid="booking-select-event-type"><SelectValue placeholder="Select event" /></SelectTrigger>
          <SelectContent>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`event-type-option-${t}`}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label htmlFor="bf-date">Event date</Label><Input id="bf-date" type="date" value={form.event_date} onChange={set("event_date")} className={field} data-testid="booking-input-date" /></div>
      <div><Label htmlFor="bf-location">Location / venue</Label><Input id="bf-location" value={form.location} onChange={set("location")} className={field} placeholder="City or venue" data-testid="booking-input-location" /></div>
      <div className="sm:col-span-2">
        <Label>Preferred package</Label>
        <Select value={form.package} onValueChange={set("package")}>
          <SelectTrigger className={field} data-testid="booking-select-package"><SelectValue placeholder="Select a package" /></SelectTrigger>
          <SelectContent>{BOOKING_PACKAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2"><Label htmlFor="bf-msg">Message</Label><Textarea id="bf-msg" rows={4} value={form.message} onChange={set("message")} className="rounded-xl border-gold/25 bg-white/80 text-[15px] focus-visible:ring-gold" placeholder="Tell us about your celebration…" data-testid="booking-input-message" /></div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-70" data-testid="booking-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {loading ? "Sending…" : "Send Inquiry"}
        </button>
      </div>
    </form>
  );
};
